// Preview Device Service
// Two-way Firestore channel for listening to a request on a second device, in any player

import { db, functions } from './firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { generateVerificationToken } from './events';
import { pickLatestSession } from '../utils/shareLinks';
import type { PreviewTrack } from '../utils/playerLinks';

// ============================================
// TYPES
// ============================================

export interface PreviewSession {
  id: string;
  eventId: string;
  djId: string;
  token: string;
  status: 'waiting' | 'connected';
  createdAt: string;
  previewTrack?: PreviewTrack | null;
  result?: {
    requestId: string;
    link: string;
    status: 'found' | 'not_found';
  } | null;
}

const COLLECTION = 'previewSessions';

// ============================================
// CRUD OPERATIONS
// ============================================

export async function createPreviewSession(
  eventId: string,
  djId: string
): Promise<{ sessionId: string; token: string }> {
  const token = generateVerificationToken();
  const docRef = await addDoc(collection(db, COLLECTION), {
    eventId,
    djId,
    token,
    status: 'waiting',
    createdAt: new Date().toISOString(),
    previewTrack: null,
    result: null,
  });
  return { sessionId: docRef.id, token };
}

export async function getPreviewSession(
  eventId: string,
  djId: string
): Promise<PreviewSession | null> {
  const q = query(
    collection(db, COLLECTION),
    where('eventId', '==', eventId),
    where('djId', '==', djId)
  );
  const snap = await getDocs(q);
  const sessions = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }) as PreviewSession);
  return pickLatestSession(sessions, false);
}

export async function connectPreviewDevice(eventId: string, token: string): Promise<string> {
  const callable = httpsCallable<{ eventId: string; token: string }, { sessionId: string }>(
    functions,
    'connectPreviewDevice'
  );
  const result = await callable({ eventId, token });
  return result.data.sessionId;
}

export async function sendTrackToPreview(
  sessionId: string,
  previewTrack: PreviewTrack
): Promise<void> {
  const ref = doc(db, COLLECTION, sessionId);
  await updateDoc(ref, { previewTrack, result: null });
}

export type PreviewResult = NonNullable<PreviewSession['result']>;

export async function submitPreviewResult(
  eventId: string,
  token: string,
  result: PreviewResult
): Promise<void> {
  const callable = httpsCallable<{ eventId: string; token: string; result: PreviewResult }, { ok: boolean }>(
    functions,
    'submitPreviewResult'
  );
  await callable({ eventId, token, result });
}

export async function clearPreviewResult(sessionId: string): Promise<void> {
  const ref = doc(db, COLLECTION, sessionId);
  await updateDoc(ref, { result: null });
}

export async function deletePreviewSession(sessionId: string): Promise<void> {
  const ref = doc(db, COLLECTION, sessionId);
  await deleteDoc(ref);
}

// ============================================
// HELPERS
// ============================================

export function generatePreviewQrUrl(eventId: string, token: string): string {
  const previewUrl = `${window.location.origin}/events/${eventId}/preview?token=${token}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(previewUrl)}`;
}

export function getPreviewPageUrl(eventId: string, token: string): string {
  return `${window.location.origin}/events/${eventId}/preview?token=${token}`;
}
